import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import { useTheme, alpha } from '@mui/material/styles';
import { Responsive, WidthProvider } from "react-grid-layout/legacy"; // legacy: CRA-kompatibler Build
import StatusDonut from './ui/StatusDonut';
import TrendTile from './ui/TrendTile';
import StatTile from './ui/StatTile';
import FunnelTile from './ui/FunnelTile';
import { DashboardSkeleton } from './ui/PageSkeleton';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import SickIcon from "@mui/icons-material/Sick";
import FlightIcon from "@mui/icons-material/Flight";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import axiosInstance from './axiosInstance';
import { useBreadcrumbs } from './BreadcrumbBar';
import { usePipelineConfig } from '../providers/PipelineConfigProvider';
import { phaseChartColors } from '../utils/phaseColors';

const ResponsiveGridLayout = WidthProvider(Responsive);

// ── Default-Layout ────────────────────────────────────────
const DEFAULT_LAYOUTS = {
  lg: [
    { i: 'kpi-total',       x: 0, y: 0,  w: 3, h: 4, minW: 2, minH: 3 },
    { i: 'kpi-new',         x: 3, y: 0,  w: 3, h: 4, minW: 2, minH: 3 },
    { i: 'kpi-conv',        x: 6, y: 0,  w: 3, h: 4, minW: 2, minH: 3 },
    { i: 'kpi-projects',    x: 9, y: 0,  w: 3, h: 4, minW: 2, minH: 3 },
    { i: 'my-tasks',        x: 0, y: 4,  w: 4, h: 8, minW: 3, minH: 5 },
    { i: 'top-leads',       x: 4, y: 4,  w: 4, h: 8, minW: 3, minH: 5 },
    { i: 'neglected-leads', x: 8, y: 4,  w: 4, h: 8, minW: 3, minH: 5 },
    { i: 'lead-pie',        x: 0, y: 12, w: 4, h: 6, minW: 3, minH: 5 },
    { i: 'lead-trend',      x: 4, y: 12, w: 4, h: 6, minW: 3, minH: 5 },
    { i: 'absent-today',    x: 8, y: 12, w: 4, h: 6, minW: 3, minH: 4 },
    { i: 'proj-pie',        x: 0, y: 18, w: 4, h: 6, minW: 3, minH: 5 },
    { i: 'notes',           x: 8, y: 18, w: 4, h: 6, minW: 3, minH: 4 },
  ],
  md: [
    { i: 'kpi-total',       x: 0, y: 0,  w: 3, h: 4 },
    { i: 'kpi-new',         x: 3, y: 0,  w: 3, h: 4 },
    { i: 'kpi-conv',        x: 6, y: 0,  w: 3, h: 4 },
    { i: 'kpi-projects',    x: 9, y: 0,  w: 3, h: 4 },
    { i: 'my-tasks',        x: 0, y: 4,  w: 4, h: 8 },
    { i: 'top-leads',       x: 4, y: 4,  w: 4, h: 8 },
    { i: 'neglected-leads', x: 8, y: 4,  w: 4, h: 8 },
    { i: 'lead-pie',        x: 0, y: 12, w: 4, h: 6 },
    { i: 'lead-trend',      x: 4, y: 12, w: 4, h: 6 },
    { i: 'absent-today',    x: 8, y: 12, w: 4, h: 6 },
    { i: 'proj-pie',        x: 0, y: 18, w: 4, h: 6 },
    { i: 'notes',           x: 8, y: 18, w: 4, h: 6 },
  ],
  sm: [
    { i: 'kpi-total',       x: 0, y: 0,  w: 3, h: 4 },
    { i: 'kpi-new',         x: 3, y: 0,  w: 3, h: 4 },
    { i: 'kpi-conv',        x: 0, y: 4,  w: 3, h: 4 },
    { i: 'kpi-projects',    x: 3, y: 4,  w: 3, h: 4 },
    { i: 'my-tasks',        x: 0, y: 8,  w: 6, h: 8 },
    { i: 'top-leads',       x: 0, y: 16, w: 6, h: 8 },
    { i: 'neglected-leads', x: 0, y: 24, w: 6, h: 8 },
    { i: 'lead-pie',        x: 0, y: 32, w: 6, h: 6 },
    { i: 'lead-trend',      x: 0, y: 38, w: 6, h: 6 },
    { i: 'absent-today',    x: 0, y: 44, w: 6, h: 6 },
    { i: 'proj-pie',        x: 0, y: 50, w: 6, h: 6 },
    { i: 'notes',           x: 0, y: 62, w: 6, h: 6 },
  ],
};

// Anzeigenamen für das Kachel-Sichtbarkeits-Menü
const TILE_LABELS = {
  'kpi-total': 'Leads gesamt',
  'kpi-new': 'Neue Leads (30 Tage)',
  'kpi-conv': 'Konversionsrate',
  'kpi-projects': 'Aktive Projekte',
  'my-tasks': 'Meine Aufgaben',
  'top-leads': 'Top-Leads',
  'neglected-leads': 'Vernachlässigte Leads',
  'absent-today': 'Heute abwesend',
  'lead-pie': 'Offene Pipeline',
  'lead-trend': 'Leads — Monatlicher Trend',
  'proj-pie': 'Projekte — Statusverteilung',
  'notes': 'Notizen',
};

const ALL_TILE_KEYS = Object.keys(TILE_LABELS);

const ABSENT_TYPES = {
  vacation:      { label: 'Urlaub',      icon: BeachAccessIcon },
  sick:          { label: 'Krankheit',   icon: SickIcon },
  business_trip: { label: 'Dienstreise', icon: FlightIcon },
};
// Icon-/Textfarbe auf alpha()-Grund — im Dunkeln hellere Pendants, sonst zu schwach
const absentColor = (type, theme) => (theme.palette.mode === 'dark'
  ? { vacation: '#58C9A2', sick: '#F09070', business_trip: '#6FAEE8' }
  : { vacation: '#1D9E75', sick: '#D85A30', business_trip: '#185FA5' })[type];

const initials = (name) =>
  (name || '?').trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

const daysSince = (dateStr) => {
  if (!dateStr) return null;
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  return isNaN(days) ? null : Math.max(0, days);
};

// Beim Laden gespeicherter Layouts fehlende (neue) Kacheln aus den
// Defaults ergänzen — sonst sehen Bestandsnutzer neue Kacheln nie.
const mergeLayouts = (saved) => {
  const merged = {};
  Object.keys(DEFAULT_LAYOUTS).forEach((bp) => {
    const savedBp = Array.isArray(saved?.[bp]) ? saved[bp] : [];
    const missing = DEFAULT_LAYOUTS[bp].filter((d) => !savedBp.some((s) => s.i === d.i));
    merged[bp] = [...savedBp, ...missing.map((m) => ({ ...m }))];
  });
  return merged;
};

// ── Kachel-Wrapper (v2.5-Fläche) ─────────────────────────
function Tile({ children, editMode, dragHandleClass, onHide }) {
  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: '14px',
        transition: 'box-shadow 0.15s',
        ...(editMode && {
          boxShadow: 3,
          outline: '2px dashed',
          outlineColor: 'primary.light',
          cursor: 'grab',
        }),
      }}
    >
      {editMode && (
        <Box
          className={dragHandleClass}
          sx={{ height: 6, bgcolor: 'primary.light', opacity: 0.5, cursor: 'grab', flexShrink: 0, '&:active': { cursor: 'grabbing' } }}
        />
      )}
      {editMode && onHide && (
        <Tooltip title="Kachel ausblenden">
          <IconButton
            size="small"
            onClick={onHide}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{ position: 'absolute', top: 4, right: 4, zIndex: 2, bgcolor: 'background.paper', boxShadow: 1, '&:hover': { bgcolor: 'error.light', color: 'error.contrastText' } }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
      <Box sx={{ p: 2.5, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {children}
      </Box>
    </Paper>
  );
}

// ── Kachel-Kopf ───────────────────────────────────────────
function TileHeader({ label, count, onLink, linkLabel }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25, flexShrink: 0 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 550, fontSize: '0.82rem', lineHeight: 1.5 }}>
        {label}{count !== undefined ? ` (${count})` : ''}
      </Typography>
      {onLink && (
        <Box onClick={onLink}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', color: 'primary.main', fontSize: '0.75rem', fontWeight: 600 }}>
          {linkLabel}
          <ArrowForwardIosIcon sx={{ fontSize: 10 }} />
        </Box>
      )}
    </Box>
  );
}

// Leerzustand innerhalb einer Kachel
const TileEmpty = ({ children }) => (
  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <Typography variant="body2" color="text.disabled">{children}</Typography>
  </Box>
);

// ── Hauptkomponente ───────────────────────────────────────
function Homepage() {
  const theme = useTheme();
  const navigate = useNavigate();
  // Pipeline-Konfiguration (B3): Phasen/Labels + Stale-Schwellen aus dem Admin
  const { phasesFor, lostFor, statusLabelsFor, staleWarnDays } = usePipelineConfig();
  const leadPhases = phasesFor('lead');
  const STATUS_LABELS = statusLabelsFor('lead');
  const projectStatusLabels = statusLabelsFor('project');

  const [editMode, setEditMode] = useState(false);
  const [layouts, setLayouts] = useState(DEFAULT_LAYOUTS);
  const [hiddenTiles, setHiddenTiles] = useState([]);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [visibilityAnchor, setVisibilityAnchor] = useState(null);

  const [currentUser, setCurrentUser] = useState(null);
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectMetrics, setProjectMetrics] = useState({ statusDistribution: [], nearestEndDate: null });
  const [notes, setNotes] = useState('');
  const [myTasks, setMyTasks] = useState([]);
  const [todayEntries, setTodayEntries] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [users, setUsers] = useState([]);

  const notesDebounceRef = useRef(null);

  const handleNotesChange = (value) => {
    setNotes(value);
    if (notesDebounceRef.current) clearTimeout(notesDebounceRef.current);
    notesDebounceRef.current = setTimeout(() => {
      axiosInstance.patch('/me/preferences/', { notes: value }).catch(() => {});
    }, 600);
  };

  useEffect(() => () => { if (notesDebounceRef.current) clearTimeout(notesDebounceRef.current); }, []);

  useBreadcrumbs([{}]);

  // ── Preferences laden ──────────────────────────────────
  useEffect(() => {
    axiosInstance.get('/me/preferences/').then(r => {
      const dl = r.data?.dashboard_layouts;
      if (dl && dl.layouts) setLayouts(mergeLayouts(dl.layouts));
      if (dl && Array.isArray(dl.hiddenTiles)) setHiddenTiles(dl.hiddenTiles);
      if (typeof r.data?.notes === 'string') setNotes(r.data.notes);
    }).catch(() => {}).finally(() => setPrefsLoaded(true));
  }, []);

  // ── Daten laden ────────────────────────────────────────
  useEffect(() => {
    axiosInstance.get('/leads/').then(r => setLeads(r.data)).catch(() => {});
    axiosInstance.get('/projects/').then(r => setProjects(r.data)).catch(() => {});
    axiosInstance.get('/users/').then(r => setUsers(r.data)).catch(() => {});
    axiosInstance.get('/projects/metrics/').then(r => {
      // Labels kommen beim Rendern aus der Pipeline-Konfiguration (B3)
      const dist = r.data.status_distribution.map(item => ({ ...item, color: getProjectStatusColor(item.id) }));
      setProjectMetrics({ statusDistribution: dist, nearestEndDate: r.data.nearest_end_date });
    }).catch(() => {});

    const today = dayjs().format('YYYY-MM-DD');
    axiosInstance.get('/team-calendar/', { params: { start: today, end: today, status: 'approved' } })
      .then(r => setTodayEntries(r.data)).catch(() => {});

    axiosInstance.get('/me/').then(r => {
      setCurrentUser(r.data);
      // Rollenbasiert: Meine offenen Aufgaben; Manager zusätzlich offene Anträge.
      axiosInstance.get('/tasks/', { params: { assignee: r.data.id, open: 1 } })
        .then(t => setMyTasks(t.data)).catch(() => {});
      if (r.data.is_staff) {
        axiosInstance.get('/team-calendar/', { params: { status: 'pending' } })
          .then(p => setPendingRequests(p.data)).catch(() => {});
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Farben ─────────────────────────────────────────────
  // Design 3.0: Chart-Farben aus der Akzent-Ramp (hell = früh, dunkel = weit)
  const leadChartColors = phaseChartColors(theme, leadPhases, lostFor('lead'));
  const getLeadStatusColor = (status) => leadChartColors[status] || theme.palette.tokens.chartNeutral;

  const projectChartColors = phaseChartColors(theme, phasesFor('project'), lostFor('project'), 'support');
  const getProjectStatusColor = (status) => projectChartColors[status] || theme.palette.tokens.chartNeutral;

  const scoreConf = (score) => ({
    Hot:  { label: 'Heiß', color: theme.palette.leadScore?.hot },
    Warm: { label: 'Warm', color: theme.palette.leadScore?.warm },
    Cold: { label: 'Kalt', color: theme.palette.leadScore?.cold },
  }[score] || null);

  const softChip = (label, color) => (
    <Chip size="small" label={label} sx={{ bgcolor: alpha(color || theme.palette.tokens.muted, 0.13), color: color || 'text.secondary', fontWeight: 600 }} />
  );

  // ── Abgeleitete Daten ──────────────────────────────────
  // Laufende Phasen = alle außer der letzten (gewonnen) — Verlust-Zweig ist ohnehin nicht enthalten
  const OPEN_STATUSES = leadPhases.slice(0, -1).map((p) => p.key);

  // Offene Pipeline: nur offene Phasen im Donut — Gewonnen/Abgelehnt sind
  // Bestand, akkumulieren für immer und würden den Donut mit der Zeit
  // dominieren; sie stehen stattdessen in der Fußzeile der Kachel.
  const openLeads = leads.filter((l) => OPEN_STATUSES.includes(l.status));
  const openStatusDist = OPEN_STATUSES
    .map((key) => ({
      id: key, label: STATUS_LABELS[key] || key,
      value: openLeads.filter((l) => l.status === key).length,
      color: getLeadStatusColor(key),
    }))
    .filter((d) => d.value > 0);
  const wonCount = leads.filter((l) => l.status === leadPhases[leadPhases.length - 1]?.key).length;
  const lostKey = lostFor('lead')?.key;
  const lostCount = lostKey ? leads.filter((l) => l.status === lostKey).length : 0;

  const thirtyDaysAgo = new Date(); thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const newLeads30d = leads.filter(l => new Date(l.created_at) >= thirtyDaysAgo).length;
  // Vorperiode gleicher Länge + Wochenverlauf — Futter für Delta-Chips & Sparkline
  const sixtyDaysAgo = dayjs().subtract(60, 'day');
  const newLeadsPrev30d = leads.filter(l => {
    const d = dayjs(l.created_at);
    return d.isAfter(sixtyDaysAgo) && new Date(l.created_at) < thirtyDaysAgo;
  }).length;
  const weeklySpark = (() => {
    const w = Array(12).fill(0);
    const nowMs = Date.now();
    leads.forEach(l => {
      const age = Math.floor((nowMs - new Date(l.created_at).getTime()) / (7 * 86400000));
      if (age >= 0 && age < 12) w[11 - age] += 1;
    });
    return w;
  })();
  const conversionRate = leads.length
    ? ((leads.filter(l => l.status === 'Closed Won').length / leads.length) * 100).toFixed(1)
    : 0;

  const monthlyTrend = useMemo(() => {
    const data = [];
    for (let i = 5; i >= 0; i--) {
      const m = dayjs().subtract(i, 'month');
      const count = leads.filter(l => dayjs(l.created_at).isSame(m, 'month')).length;
      data.push({ x: m.format('MMM'), y: count });
    }
    return [{ id: 'Leads', data }];
  }, [leads]);
  const sixSum = monthlyTrend[0].data.reduce((a, p) => a + p.y, 0);
  const prevSixSum = leads.filter(l => {
    const d = dayjs(l.created_at);
    return d.isAfter(dayjs().subtract(12, 'month')) && d.isBefore(dayjs().subtract(6, 'month'));
  }).length;

  const activeProjects = projects.filter(p => p.status === 'In Progress').length;

  // Trichter (wie in der Lead-Liste): wie viele Leads sind MINDESTENS bis
  // Phase i gekommen — kumulativ über status_history + aktuellen Status
  const leadFunnel = useMemo(() => {
    const phaseIdx = Object.fromEntries(leadPhases.map((p, i) => [p.key, i]));
    const maxIdx = (l) => {
      const touched = [l.status, ...(Array.isArray(l.status_history) ? l.status_history.map((h) => h.to) : [])];
      return touched.reduce((m, k) => (phaseIdx[k] !== undefined && phaseIdx[k] > m ? phaseIdx[k] : m), -1);
    };
    return leadPhases.map((p, i) => ({
      key: p.key, label: p.label, color: leadChartColors[p.key],
      count: leads.filter((l) => maxIdx(l) >= i).length,
    }));
  }, [leads, leadPhases, leadChartColors]);

  // Zeitfortschritt eines Projekts (Muster aus der Projektliste)
  const timeProgress = (start, end) => {
    const total = dayjs(end).diff(dayjs(start), 'day');
    if (!total || total <= 0) return dayjs().isAfter(dayjs(end)) ? 100 : 0;
    const elapsed = dayjs().diff(dayjs(start), 'day');
    return Math.max(0, Math.min(100, (elapsed / total) * 100));
  };
  const nextEndingProjects = useMemo(() => (
    projects
      .filter((p) => p.status === 'In Progress')
      .sort((a, b) => new Date(a.end_date) - new Date(b.end_date))
      .slice(0, 2)
  ), [projects]);

  // Top-Leads: offene Leads, heißeste zuerst
  const topLeads = useMemo(() => {
    const rank = { Hot: 0, Warm: 1, Cold: 2 };
    return leads
      .filter(l => OPEN_STATUSES.includes(l.status))
      .sort((a, b) => {
        const r = (rank[a.score] ?? 3) - (rank[b.score] ?? 3);
        return r !== 0 ? r : new Date(b.created_at) - new Date(a.created_at);
      })
      .slice(0, 5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads]);

  // Vernachlässigt: offene Leads, die seit >= Warn-Schwelle in ihrer Phase stehen
  const neglectedLeads = useMemo(() => {
    return leads
      .filter(l => OPEN_STATUSES.includes(l.status))
      .map(l => ({ ...l, _days: daysSince(l.phase_changed_at) }))
      .filter(l => l._days !== null && l._days >= staleWarnDays)
      .sort((a, b) => b._days - a._days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads]);

  // Heute abwesend (nur echte Abwesenheits-Typen)
  const absentToday = useMemo(() => {
    return todayEntries
      .filter(e => ABSENT_TYPES[e.type])
      .map(e => {
        const u = users.find(x => x.id === e.employee);
        return { ...e, _name: u ? (u.first_name ? `${u.first_name} ${u.last_name}` : u.username) : '—' };
      });
  }, [todayEntries, users]);

  const overdueTasks = myTasks.filter(t => t.due_date && dayjs(t.due_date).isBefore(dayjs(), 'day'));

  const completeTask = (task) => {
    axiosInstance.patch(`/tasks/${task.id}/`, { completed_at: new Date().toISOString() })
      .then(() => setMyTasks(prev => prev.filter(t => t.id !== task.id)))
      .catch(() => {});
  };

  const taskTarget = (task) => {
    const info = task.related_object_info;
    if (!info) return null;
    const route = { lead: '/leads', account: '/accounts', contact: '/contacts', project: '/projects' }[info.type];
    return route ? `${route}/${info.id}` : null;
  };

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Guten Morgen' : hour < 18 ? 'Guten Tag' : 'Guten Abend';

  // ── Persistenz ─────────────────────────────────────────
  const persistPreferences = useCallback((nextLayouts, nextHidden) => {
    axiosInstance.patch('/me/preferences/', {
      dashboard_layouts: { layouts: nextLayouts, hiddenTiles: nextHidden },
    }).catch(() => {});
  }, []);

  const handleLayoutChange = useCallback((_layout, allLayouts) => {
    if (!prefsLoaded) return;
    setLayouts(allLayouts);
    persistPreferences(allLayouts, hiddenTiles);
  }, [prefsLoaded, hiddenTiles, persistPreferences]);

  const handleReset = () => {
    setLayouts(DEFAULT_LAYOUTS);
    setHiddenTiles([]);
    persistPreferences(DEFAULT_LAYOUTS, []);
  };

  const handleHideTile = (key) => {
    const next = [...hiddenTiles, key];
    setHiddenTiles(next);
    persistPreferences(layouts, next);
  };

  const handleToggleTile = (key) => {
    const isHidden = hiddenTiles.includes(key);
    if (isHidden) {
      const nextLayouts = mergeLayouts(layouts);
      const next = hiddenTiles.filter(k => k !== key);
      setLayouts(nextLayouts);
      setHiddenTiles(next);
      persistPreferences(nextLayouts, next);
    } else {
      const next = [...hiddenTiles, key];
      setHiddenTiles(next);
      persistPreferences(layouts, next);
    }
  };

  const DRAG_HANDLE = 'tile-drag-handle';

  // Kompakte klickbare Zeile in Listen-Kacheln
  const rowSx = (clickable = true) => ({
    display: 'flex', alignItems: 'center', gap: 1.25, py: 0.75, minWidth: 0,
    borderTop: '1px solid', borderColor: 'divider',
    '&:first-of-type': { borderTop: 'none' },
    ...(clickable && { cursor: 'pointer', '&:hover': { bgcolor: 'action.hover', mx: -1, px: 1, borderRadius: 1 } }),
  });

  // ── Kachel-Inhalte ─────────────────────────────────────
  const tiles = {
    'kpi-total': (
      <StatTile label="Leads gesamt" value={leads.length} onClick={() => navigate('/leads')}
        delta={newLeads30d} deltaLabel="30 Tage" spark={weeklySpark} />
    ),
    'kpi-new': (
      <StatTile label="Neue Leads · 30 Tage" value={newLeads30d} onClick={() => navigate('/leads')}
        delta={newLeads30d - newLeadsPrev30d} deltaLabel="vs. Vorperiode" />
    ),
    'kpi-conv': (
      <FunnelTile
        label="Konversionsrate"
        value={`${conversionRate}%`}
        steps={leadFunnel}
        onStepClick={(key) => navigate(`/leads?phase=${encodeURIComponent(key)}`)}
      />
    ),
    'kpi-projects': (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 550, fontSize: '0.82rem', lineHeight: 1.5 }} noWrap>
          Aktive Projekte
        </Typography>
        <Typography onClick={() => navigate('/projects')}
          sx={{ fontSize: '1.35rem', fontWeight: 700, color: 'text.primary', fontVariantNumeric: 'tabular-nums', lineHeight: 1.2, cursor: 'pointer', alignSelf: 'flex-start' }}>
          {activeProjects}
        </Typography>
        {nextEndingProjects.length === 0 ? (
          <TileEmpty>Keine aktiven Projekte.</TileEmpty>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.75, mt: 0.5 }}>
            {nextEndingProjects.map((p) => {
              const daysLeft = dayjs(p.end_date).startOf('day').diff(dayjs().startOf('day'), 'day');
              const overdue = daysLeft < 0;
              return (
                <Box key={p.id} onClick={() => navigate(`/projects/${p.id}`)}
                  sx={{ cursor: 'pointer', borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, fontSize: '0.72rem' }}>
                    <Typography component="span" noWrap sx={{ fontSize: 'inherit', fontWeight: 600, minWidth: 0 }}>{p.name}</Typography>
                    <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 650, flex: 'none', fontVariantNumeric: 'tabular-nums', color: overdue ? 'error.main' : 'text.secondary' }}>
                      {overdue ? 'überfällig' : daysLeft === 0 ? 'heute' : `${daysLeft} Tg.`}
                    </Typography>
                  </Box>
                  <Box sx={{ height: 6, borderRadius: 3, bgcolor: theme.palette.tokens.ramp2[100], mt: 0.4 }}>
                    <Box sx={{ height: 6, borderRadius: 3, width: `${timeProgress(p.start_date, p.end_date)}%`, bgcolor: overdue ? 'error.main' : theme.palette.tokens.ramp2[500] }} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    ),

    'my-tasks': (
      <>
        <TileHeader label="Meine Aufgaben" count={myTasks.length} />
        {myTasks.length === 0 ? (
          <TileEmpty>Keine offenen Aufgaben — gut gemacht.</TileEmpty>
        ) : (
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {myTasks.map((t) => {
              const overdue = t.due_date && dayjs(t.due_date).isBefore(dayjs(), 'day');
              const dueToday = t.due_date && dayjs(t.due_date).isSame(dayjs(), 'day');
              const target = taskTarget(t);
              return (
                <Box key={t.id} sx={rowSx(!!target)} onClick={() => target && navigate(target)}>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); completeTask(t); }}
                    sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'success.main' } }}
                    aria-label="Aufgabe erledigen"
                  >
                    <CircleOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{t.title}</Typography>
                    {t.related_object_info && (
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                        {t.related_object_info.name}
                      </Typography>
                    )}
                  </Box>
                  {t.due_date && (
                    <Typography variant="caption" sx={{
                      color: overdue ? 'error.main' : dueToday ? 'warning.main' : 'text.secondary',
                      fontWeight: overdue || dueToday ? 700 : 400, whiteSpace: 'nowrap',
                    }}>
                      {overdue ? `seit ${dayjs(t.due_date).format('DD.MM.')}` : dueToday ? 'heute' : dayjs(t.due_date).format('DD.MM.')}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </>
    ),

    'top-leads': (
      <>
        <TileHeader label="Top-Leads" onLink={() => navigate('/leads')} linkLabel="Alle" />
        {topLeads.length === 0 ? (
          <TileEmpty>Keine offenen Leads.</TileEmpty>
        ) : (
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {topLeads.map((l) => {
              const sc = scoreConf(l.score);
              return (
                <Box key={l.id} sx={rowSx()} onClick={() => navigate(`/leads/${l.id}`)}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', fontWeight: 700, bgcolor: 'primary.light', color: 'primary.dark' }}>
                    {initials(`${l.firstName || ''} ${l.lastName || ''}`)}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{`${l.firstName || ''} ${l.lastName || ''}`.trim() || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                      {l.company || STATUS_LABELS[l.status] || l.status}
                    </Typography>
                  </Box>
                  {sc && softChip(sc.label, sc.color)}
                </Box>
              );
            })}
          </Box>
        )}
      </>
    ),

    'neglected-leads': (
      <>
        <TileHeader label="Vernachlässigte Leads" count={neglectedLeads.length} onLink={() => navigate('/leads')} linkLabel="Alle" />
        {neglectedLeads.length === 0 ? (
          <TileEmpty>Nichts liegt liegen — stark.</TileEmpty>
        ) : (
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {neglectedLeads.slice(0, 6).map((l) => (
              <Box key={l.id} sx={rowSx()} onClick={() => navigate(`/leads/${l.id}`)}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{`${l.firstName || ''} ${l.lastName || ''}`.trim() || '—'}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {STATUS_LABELS[l.status] || l.status}{l.company ? ` · ${l.company}` : ''}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 700, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  {l._days} Tage
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </>
    ),

    'absent-today': (
      <>
        <TileHeader label="Heute abwesend" onLink={() => navigate('/team-calendar')} linkLabel="Kalender" />
        {absentToday.length === 0 ? (
          <TileEmpty>Alle an Bord.</TileEmpty>
        ) : (
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {absentToday.map((e) => {
              const conf = ABSENT_TYPES[e.type];
              const Icon = conf.icon;
              const color = absentColor(e.type, theme);
              return (
                <Box key={e.id} sx={rowSx()} onClick={() => navigate('/team-calendar')}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', fontWeight: 700, bgcolor: alpha(color, 0.15), color }}>
                    {initials(e._name)}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{e._name}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                      {conf.label}{e.end_date !== e.start_date ? ` · bis ${dayjs(e.end_date).format('DD.MM.')}` : ''}
                    </Typography>
                  </Box>
                  <Icon sx={{ fontSize: 18, color }} />
                </Box>
              );
            })}
          </Box>
        )}
      </>
    ),

    'lead-pie': (
      <>
        <TileHeader label="Offene Pipeline" onLink={() => navigate('/leads')} linkLabel="Alle" />
        {openStatusDist.length === 0 ? (
          <TileEmpty>Keine offenen Leads.</TileEmpty>
        ) : (
          /* Donut 2.0: Zentrum-KPI + Wertelegende; Segment-Klick springt gefiltert in die Leads */
          <StatusDonut data={openStatusDist} centerLabel="offen"
            onSliceClick={(id) => navigate(`/leads?phase=${encodeURIComponent(id)}`)} />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ pt: 0.75, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
          Gesamt: {wonCount} gewonnen · {lostCount} verloren
        </Typography>
      </>
    ),

    'lead-trend': (
      <>
        <TrendTile
          label="Neue Leads — 6 Monate"
          points={monthlyTrend[0].data}
          value={sixSum}
          delta={sixSum - prevSixSum}
          deltaLabel="vs. Vorhalbjahr"
        />
      </>
    ),

    'proj-pie': (
      <>
        <TileHeader label="Projekte — Statusverteilung" onLink={() => navigate('/projects')} linkLabel="Alle" />
        <StatusDonut
          data={projectMetrics.statusDistribution.map((item) => ({ ...item, label: projectStatusLabels[item.id] || item.id }))}
          centerLabel="Projekte"
          onSliceClick={() => navigate('/projects')}
        />
      </>
    ),

    'notes': (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <TileHeader label="Notizen" />
        <TextField
          multiline fullWidth placeholder="Notizen eintragen…"
          value={notes} onChange={(e) => handleNotesChange(e.target.value)}
          variant="standard" InputProps={{ disableUnderline: true }}
          sx={{
            flex: 1,
            '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' },
            '& .MuiInputBase-input': { height: '100% !important', overflow: 'auto !important' },
          }}
        />
      </Box>
    ),
  };

  const visibleTiles = useMemo(
    () => Object.entries(tiles).filter(([key]) => !hiddenTiles.includes(key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tiles, hiddenTiles]
  );

  const visibleLayouts = useMemo(() => {
    const result = {};
    Object.entries(layouts).forEach(([bp, items]) => {
      result[bp] = items.filter(item => !hiddenTiles.includes(item.i));
    });
    return result;
  }, [layouts, hiddenTiles]);

  // ── „Heute wichtig" — feste Cockpit-Zeile ──────────────
  const cockpitItems = [];
  if (currentUser?.is_staff && pendingRequests.length > 0) {
    cockpitItems.push({
      key: 'requests',
      label: `${pendingRequests.length} Urlaubsantr${pendingRequests.length === 1 ? 'ag wartet' : 'äge warten'} auf Freigabe`,
      color: theme.palette.warning.main,
      onClick: () => navigate('/team-calendar'),
    });
  }
  if (overdueTasks.length > 0) {
    cockpitItems.push({
      key: 'overdue',
      label: `${overdueTasks.length} Aufgabe${overdueTasks.length === 1 ? '' : 'n'} überfällig`,
      color: theme.palette.error.main,
    });
  }
  if (neglectedLeads.length > 0) {
    cockpitItems.push({
      key: 'neglected',
      label: `${neglectedLeads.length} Lead${neglectedLeads.length === 1 ? '' : 's'} vernachlässigt`,
      color: theme.palette.error.main,
      onClick: () => navigate('/leads'),
    });
  }
  if (absentToday.length > 0) {
    cockpitItems.push({
      key: 'absent',
      label: `Heute abwesend: ${absentToday.map(a => a._name.split(' ')[0]).join(', ')}`,
      color: theme.palette.info?.main || theme.palette.primary.main,
      onClick: () => navigate('/team-calendar'),
    });
  }

  return (
    <Box>
      {/* Kopf */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            {greeting}{currentUser?.first_name ? `, ${currentUser.first_name}` : ''}.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {dayjs().locale('de').format('dddd, D. MMMM YYYY')}
          </Typography>
        </Box>

        {/* Layout-Steuerung */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {editMode && (
            <Tooltip title="Kacheln ein-/ausblenden">
              <IconButton size="small" onClick={(e) => setVisibilityAnchor(e.currentTarget)}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {editMode && (
            <Tooltip title="Layout zurücksetzen">
              <IconButton size="small" onClick={handleReset}>
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={editMode ? 'Layout sperren' : 'Layout bearbeiten'}>
            <IconButton size="small" onClick={() => setEditMode(e => !e)} color={editMode ? 'primary' : 'default'}>
              {editMode ? <LockOpenOutlinedIcon fontSize="small" /> : <LockOutlinedIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          {editMode && (
            <Typography variant="caption" color="primary.main" sx={{ fontWeight: 500 }}>
              Bearbeitungsmodus
            </Typography>
          )}
        </Box>
      </Box>

      {/* „Heute wichtig" — fest, nicht konfigurierbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2.5 }}>
        {cockpitItems.length === 0 ? (
          <Chip
            size="small"
            icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
            label="Alles im grünen Bereich — keine offenen Aktionen."
            sx={{ bgcolor: alpha(theme.palette.success.main, 0.12), color: theme.palette.success.main, fontWeight: 600, '& .MuiChip-icon': { color: theme.palette.success.main } }}
          />
        ) : (
          cockpitItems.map((item) => (
            <Chip
              key={item.key}
              size="small"
              label={item.label}
              onClick={item.onClick}
              sx={{
                bgcolor: alpha(item.color, 0.12), color: item.color, fontWeight: 600,
                cursor: item.onClick ? 'pointer' : 'default',
                ...(item.onClick && { '&:hover': { bgcolor: alpha(item.color, 0.2) } }),
              }}
            />
          ))
        )}
      </Box>

      {/* Sichtbarkeits-Popover */}
      <Popover
        open={Boolean(visibilityAnchor)}
        anchorEl={visibilityAnchor}
        onClose={() => setVisibilityAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, minWidth: 220 }}>
          <Typography variant="overline" color="text.secondary">Kacheln</Typography>
          <FormGroup sx={{ mt: 1 }}>
            {ALL_TILE_KEYS.map(key => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox size="small" checked={!hiddenTiles.includes(key)} onChange={() => handleToggleTile(key)} />
                }
                label={<Typography variant="body2">{TILE_LABELS[key]}</Typography>}
              />
            ))}
          </FormGroup>
        </Box>
      </Popover>

      {/* Grid */}
      {!prefsLoaded ? (
        <DashboardSkeleton />
      ) : (
        <ResponsiveGridLayout
          className="layout"
          layouts={visibleLayouts}
          breakpoints={{ lg: 1200, md: 996, sm: 768 }}
          cols={{ lg: 12, md: 12, sm: 6 }}
          rowHeight={30}
          isDraggable={editMode}
          isResizable={editMode}
          resizeHandles={['se']}
          isBounded={true}
          onLayoutChange={handleLayoutChange}
          margin={[12, 12]}
        >
          {visibleTiles.map(([key, content]) => (
            <div key={key}>
              <Tile editMode={editMode} dragHandleClass={DRAG_HANDLE} onHide={() => handleHideTile(key)}>
                {content}
              </Tile>
            </div>
          ))}
        </ResponsiveGridLayout>
      )}
    </Box>
  );
}

export default Homepage;
